import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Typography } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import type { CatalogImportNameAction } from '@/api/models';
import { countParam, renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NameOption } from '../../nameOptions';
import type { NameKind } from '../../namesDecisions';
import type { NameRowModel, PickerKey } from '../../useNamesStep';
import { NameAxes } from './NameAxes';
import { NameConsequence } from './NameConsequence';
import { NameRowNote } from './NameRowNote';

type NameRowProps = {
    kind: NameKind;
    actions: readonly CatalogImportNameAction[];
    row: NameRowModel;
    existing: NameOption[];
    parents: NameOption[];
    isCatalogLoading: boolean;
    onActionChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onTargetChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onNameChange: (kind: NameKind, text: string, name: string) => void;
    onParentChange: (kind: NameKind, text: string, key: PickerKey) => void;
    onAddAxesChange: (kind: NameKind, text: string, addAxes: boolean) => void;
};

const ACTION_LABEL_KEY: Record<CatalogImportNameAction, string> = {
    create: 'catalogImport.names.actions.create',
    useExisting: 'catalogImport.names.actions.useExisting',
    rename: 'catalogImport.names.actions.rename',
    nest: 'catalogImport.names.actions.nest',
};

/**
 * One name of the file: its text and row count (with "looks like a typo ·
 * …" when the preview suggests a correction, or why a cell that lists
 * several suppliers was suggested the first), the action control (Create ·
 * Use existing · Rename, and Nest under for categories) and what the action
 * does, with the axes checkbox when the category gains axes. A name the
 * catalogue already has (once, or several times) shows the control disabled
 * on Use existing, so the columns of a section stay aligned, and a line
 * under its text says why. Four columns from `lg`, stacked below. A name
 * the last check could not resolve as decided says why under it.
 */
export const NameRow: React.FC<NameRowProps> = ({
    kind,
    actions,
    row,
    existing,
    parents,
    isCatalogLoading,
    onActionChange,
    onTargetChange,
    onNameChange,
    onParentChange,
    onAddAxesChange,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="grid grid-cols-1 items-center gap-x-4 gap-y-3 px-4 py-3 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_96px_auto_minmax(0,1.4fr)]"
            {...getNodeTestIdProp(
                `catalog-import-names-row-${kind}-${row.text}`,
            )}
        >
            <div className="flex min-w-0 items-baseline justify-between gap-3 lg:contents">
                <div className="flex min-w-0 flex-col gap-0.5">
                    <Typography className="min-w-0 break-words" weight="medium">
                        {row.text}
                    </Typography>
                    {row.suggestion ? (
                        <NameRowNote note="typo">
                            {t('catalogImport.names.typo', {
                                name: row.suggestion,
                            })}
                        </NameRowNote>
                    ) : null}
                    {row.joinedParts === null ? null : (
                        <NameRowNote note="joined">
                            {t('catalogImport.names.joinedWhy', {
                                parts: countParam(row.joinedParts),
                            })}
                        </NameRowNote>
                    )}
                    {row.match === 'new' ? null : (
                        <NameRowNote note="match">
                            {row.match === 'existing'
                                ? t('catalogImport.names.match.existing')
                                : t('catalogImport.names.match.ambiguous')}
                        </NameRowNote>
                    )}
                </div>
                <Typography
                    className="shrink-0 tabular-nums"
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp('catalog-import-names-row-count')}
                >
                    {t('catalogImport.names.rows', {
                        count: row.rows,
                        rows: countParam(row.rows),
                    })}
                </Typography>
            </div>
            <div className="min-w-0 overflow-x-auto">
                <Segment
                    aria-label={t('catalogImport.names.actionsAria', {
                        text: row.text,
                    })}
                    isDisabled={row.match !== 'new'}
                    onSelectionChange={(key) =>
                        onActionChange(kind, row.text, key)
                    }
                    selectedKey={row.draft.action}
                    size="sm"
                    {...getNodeTestIdProp('catalog-import-names-actions')}
                >
                    {actions.map((action) => (
                        <Segment.Item
                            id={action}
                            key={action}
                            {...getNodeTestIdProp(
                                `catalog-import-names-action-${action}`,
                            )}
                        >
                            <Segment.Separator />
                            {t(ACTION_LABEL_KEY[action])}
                        </Segment.Item>
                    ))}
                </Segment>
            </div>
            <div className="flex min-w-0 items-center gap-2">
                <Typography aria-hidden color="muted" type="body-sm">
                    →
                </Typography>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <NameConsequence
                        existing={existing}
                        isCatalogLoading={isCatalogLoading}
                        kind={kind}
                        onNameChange={(name) =>
                            onNameChange(kind, row.text, name)
                        }
                        onParentChange={(key) =>
                            onParentChange(kind, row.text, key)
                        }
                        onTargetChange={(key) =>
                            onTargetChange(kind, row.text, key)
                        }
                        parents={parents}
                        row={row}
                    />
                    {row.axesToAdd.length > 0 ? (
                        <NameAxes
                            axes={row.axesToAdd}
                            isSelected={row.draft.addAxes}
                            onChange={(addAxes) =>
                                onAddAxesChange(kind, row.text, addAxes)
                            }
                        />
                    ) : null}
                </div>
            </div>
            {row.notices.length > 0 ? (
                <Alert
                    className="lg:col-span-4"
                    status="danger"
                    {...getNodeTestIdProp('catalog-import-names-row-problem')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        {row.notices.map((notice) => (
                            <Alert.Description key={notice.key}>
                                {renderNotice(t, notice)}
                            </Alert.Description>
                        ))}
                    </Alert.Content>
                </Alert>
            ) : null}
        </div>
    );
};
